import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, WifiOff, ShieldAlert, Clock } from 'lucide-react';
import { useDelayedAccessDenied } from '@/hooks/useDelayedAccessDenied';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layouts/AppLayout';
import { Button } from '@/components/ui/button';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'platform_admin' | 'tenant_admin' | 'supervisor' | 'agent';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const {
    user,
    userProfile,
    session,
    loading,
    databaseValidated,
    validationError,
    validationErrorKind,
    isRetryingConnection,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');

  // Check if role access should be denied
  const shouldDenyRoleAccess = requiredRole
    && userProfile?.role !== requiredRole
    && userProfile?.role !== 'platform_admin';

  // Use delayed access denied to prevent flash during initial load
  const { showDenied: showRoleDenied, isInGracePeriod: roleCheckPending } = useDelayedAccessDenied(
    !!shouldDenyRoleAccess,
    loading || !databaseValidated,
    1000
  );

  // A validation failure is no longer a single bucket: a network hiccup
  // retries in place, a permission problem is an account issue shown
  // inline, and only an actually expired/invalid session sends the user
  // back to the login screen (T-08).
  const isNetworkIssue = validationErrorKind === 'network';
  const isPermissionIssue = validationErrorKind === 'permission';
  const isExpiredSession = !!validationError && validationErrorKind === 'expired';

  useEffect(() => {
    // Only redirect when loading is complete to prevent race conditions
    if (!loading) {
      // Check for both user and session to ensure full authentication
      if (!user || !session) {
        // No session at all: this is a real "please sign in", not an
        // expulsion, so the previous location is still worth remembering
        // in case the user came from a deep link.
        navigate('/auth', { state: { from: location, reason: 'expired' } });
        return;
      }

      // Only a genuinely expired/invalid session sends the user away.
      // Network and permission failures are handled inline below without
      // ever touching navigation, so they cannot drop the destination or
      // wipe whatever state the screen already had (T-09, T-10).
      if (isExpiredSession) {
        navigate('/auth', { state: { from: location, reason: 'expired' } });
        return;
      }

      // For authenticated users: only check roles if database validation is complete AND we have a role requirement
      // CRITICAL: Must check userProfile exists first to prevent race condition redirect before profile loads
      if (databaseValidated && userProfile && requiredRole && userProfile.role !== requiredRole && userProfile.role !== 'platform_admin') {
        // ProtectedRoute: Role check failed, redirecting to dashboard
        navigate('/dashboard', { replace: true });
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userProfile, session, loading, databaseValidated, isExpiredSession, navigate, requiredRole]);

  // Show loading state while authentication is in progress OR waiting for database validation
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('loadingAuthentication')}</p>
        </div>
      </div>
    );
  }

  // A network or data-layer hiccup: keep the user on this same loading
  // shell and say so, instead of bouncing them to the login screen while
  // the connection recovers on its own (T-08).
  if (isNetworkIssue || isRetryingConnection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <WifiOff className="h-8 w-8 mx-auto mb-4 text-muted-foreground" />
          <p className="font-medium mb-1">{t('offlineRetryingTitle')}</p>
          <p className="text-sm text-muted-foreground">{t('offlineRetryingMessage')}</p>
        </div>
      </div>
    );
  }

  // The session is valid but the account does not have the DB permission
  // this view needs. This is an account problem, not a login problem --
  // show it inside the app shell instead of sending the user to /auth
  // (T-08, and the read-only-session case T-11 asks for).
  if (isPermissionIssue) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center p-6">
          <div className="max-w-md text-center">
            <ShieldAlert className="h-10 w-10 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold mb-2">{t('permissionIssueTitle')}</h2>
            <p className="text-muted-foreground mb-6">{t('permissionIssueMessage')}</p>
            <Button onClick={() => navigate('/dashboard')}>{t('backToMyBusiness')}</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // An expired session is a thing that ALREADY happened, not a wait: a spinner
  // there tells the person to keep waiting for something that will never
  // resolve on its own. Say what happened and give them the way back in (A-58).
  if (isExpiredSession) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <Clock className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold mb-2">{t('sessionExpiredTitle')}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('sessionExpiredMessage')}</p>
          <Button onClick={() => navigate('/auth', { state: { from: location, reason: 'expired' } })}>
            {t('signInAgain')}
          </Button>
        </div>
      </div>
    );
  }

  // No session at all: the redirect to /auth is already on its way from the
  // effect above, so this shell is genuinely transient and a spinner is honest.
  if (!user || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('redirecting')}</p>
        </div>
      </div>
    );
  }

  // Show loading during role check grace period (prevents flash on initial load)
  if (roleCheckPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('validatingPermissions')}</p>
        </div>
      </div>
    );
  }

  // Check role permissions (delayed to prevent flash). Rendered inside the
  // app shell, with the sidebar and header still in place, and a real way
  // out instead of a dead end (T-13).
  if (showRoleDenied) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center p-6">
          <div className="max-w-md text-center">
            <ShieldAlert className="h-10 w-10 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold mb-2">{t('accessDenied')}</h2>
            <p className="text-muted-foreground mb-2">{t('accessDeniedMessage')}</p>
            {requiredRole && (
              <p className="text-sm text-muted-foreground mb-6">
                {t('accessDeniedRoleRequired', { role: t(`roles.${requiredRole}`) })}
              </p>
            )}
            <Button onClick={() => navigate('/dashboard')}>{t('backToMyBusiness')}</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // All checks passed, render the protected content
  return <>{children}</>;
}
